import React, { Suspense } from 'react';
import { Flex } from 'glints-aries/es';
import { Icon, Typography } from 'glints-aries/es/@next';
import { Blue, Neutral } from 'glints-aries/es/@next/utilities/colors';
import { space8 } from 'glints-aries/es/@next/utilities/spacing';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { GlintsLogoRed } from '../../../common/color';
import { ROUTES } from '../../../common/routes';
import ErrorBoundaryWithCrashReporting from '../../../components/Error/ErrorBoundaryWithCrashReporting';
import { useGetCompanyCreditInventorySuspenseQuery } from '../hooks/useGetCompanyCreditInventory';
import useTopUpGlintsCreditModal from '../TopUpGlintsCreditModal/hooks/useTopUpGlintsCreditModal';

type CreditBalancePopoverMenuItemProps = {
  closePopover: () => void;
};

function CreditBalancePopoverMenuItemComponent({
  closePopover,
}: CreditBalancePopoverMenuItemProps) {
  const { data } = useGetCompanyCreditInventorySuspenseQuery('cache-first');
  const { formatNumber } = useIntl();
  const amount = data.totalAmount;
  const nextExpiringCredit = data.nextExpiringCredit;
  const hasCreditBalance = amount > 0;
  const showTopUpButton = !hasCreditBalance;
  const showNextExpiringCredit =
    hasCreditBalance && (nextExpiringCredit?.amount ?? 0) > 0;
  const { pathname } = useLocation();
  const isActivePage = new RegExp(`^(/${ROUTES.features}).*`).test(pathname);
  const { TopUpGlintsCreditModal, openModal } = useTopUpGlintsCreditModal();

  const handleClosePopover = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    closePopover();
  };

  const topUpButton = (
    <div
      onClick={e => {
        e.preventDefault();
        handleClosePopover(e);
        openModal();
      }}
    >
      <Typography
        variant="button"
        color={Blue.S99}
        style={{
          cursor: 'pointer',
        }}
      >
        <FormattedMessage id="interactive-top-up" defaultMessage="Top Up" />
      </Typography>
    </div>
  );

  const content = (
    <Flex
      style={{
        padding: space8,
        gap: space8,
        cursor: 'pointer',
      }}
    >
      <Icon
        name="ri-glints-logo-fill"
        fill={hasCreditBalance ? GlintsLogoRed : Neutral.B40}
        height={24}
      />
      <div>
        <Typography variant="body1" color={Neutral.B18}>
          <FormattedMessage
            id="text-credit-balance-credit-count"
            defaultMessage="Credit Balance: {creditCount}"
            values={{
              creditCount: formatNumber(amount),
            }}
          />
        </Typography>
        <If condition={showTopUpButton}>{topUpButton}</If>

        <If condition={showNextExpiringCredit && Boolean(nextExpiringCredit)}>
          <Typography variant="subtitle2" color={Neutral.B40}>
            <FormattedMessage
              id="text-count-credits-expires-on"
              defaultMessage="{count} Credits expires on: {expiryDate}"
              values={{
                count: nextExpiringCredit?.amount,
                expiryDate: (
                  <FormattedDate
                    value={nextExpiringCredit?.expiryDateTime}
                    day="2-digit"
                    month="short"
                    year="numeric"
                  />
                ),
              }}
            />
          </Typography>
        </If>
      </div>
    </Flex>
  );

  if (isActivePage) {
    return (
      <>
        {TopUpGlintsCreditModal}

        <div
          style={{
            cursor: 'pointer',
          }}
          onClick={handleClosePopover}
        >
          {content}
        </div>
      </>
    );
  }

  return (
    <>
      {TopUpGlintsCreditModal}
      <Link to={`/${ROUTES.features}`} onClick={handleClosePopover}>
        {content}
      </Link>
    </>
  );
}

export default function CreditBalancePopoverMenuItem(
  props: CreditBalancePopoverMenuItemProps
) {
  return (
    <ErrorBoundaryWithCrashReporting fallback={null}>
      <Suspense fallback={null}>
        <CreditBalancePopoverMenuItemComponent {...props} />
      </Suspense>
    </ErrorBoundaryWithCrashReporting>
  );
}
